import { useMutation } from '@tanstack/react-query';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import { ChangeOperatorStatusPayload } from '../../../types/operator';
import { useSnackbar } from 'notistack';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

const OperatorStateToggleDialog = ({
  operator,
  onClose,
  onConfirm,
}: {
  operator: ChangeOperatorStatusPayload | null;
  onClose: () => void;
  onConfirm: (updatedOperator: ChangeOperatorStatusPayload) => void;
}) => {
  const { updateOperatorState } = useIotsApi();
  const updateOperatorStateMutation = useMutation({
    mutationFn: updateOperatorState,
  });
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();
  const { t } = useLanguage();

  return (
    <ConfirmationDialog
      open={Boolean(operator)}
      loading={updateOperatorStateMutation.isPending}
      onClose={onClose}
      onConfirm={() => {
        if (!operator) {
          return;
        }
        const updatedOperator = {
          ...operator,
          isActive: !operator.isActive,
        };
        updateOperatorStateMutation.mutate(updatedOperator, {
          onSuccess: () => {
            enqueueSnackbar({
              variant: 'success',
              message: t('content.operator-update-success'),
            });
            onConfirm(updatedOperator);
            onClose();
          },
          onError: error => {
            enqueueSnackbar({
              variant: 'error',
              message: getErrorMessage(
                error,
                t('content.operator-update-error'),
              ),
            });
            updateOperatorStateMutation.reset();
          },
        });
      }}
    >
      {operator
        ? t(
            `content.${
              operator.isActive
                ? 'operator-deactivation-confirmation-request'
                : 'operator-activation-confirmation-request'
            }`,
          )
        : null}
    </ConfirmationDialog>
  );
};

export default OperatorStateToggleDialog;
